import { TableRow, TableCell, Typography } from '@mui/material';
import { AnyPlan } from './types';

type GlobalTotalsRowProps = {
  plans: AnyPlan[];
  bg: string;
  formatBudget: (v?: number, currency?: string) => string;
  getGlobalTotals: (plan: AnyPlan) => {
    totalBudget: number;
    techCosts: number;
  };
  firstColWidth?: number;
  showOnlyDifferences?: boolean;
};

function isDifferentFromBase<T>(
  values: (T | undefined | null)[],
  index: number,
  equals: (a: T, b: T) => boolean = (a, b) => a === b
): boolean {
  if (index === 0 || values.length === 0) return false;
  const base = values[0];
  const current = values[index];

  if (base == null && current == null) return false;
  if ((base == null) !== (current == null)) return true;

  return !equals(base as T, current as T);
}

function hasAnyDifference<T>(
  values: (T | undefined | null)[],
  equals?: (a: T, b: T) => boolean
): boolean {
  for (let i = 1; i < values.length; i++) {
    if (isDifferentFromBase(values, i, equals as any)) return true;
  }
  return false;
}

export default function GlobalTotalsRow({
  plans,
  bg,
  formatBudget,
  getGlobalTotals,
  firstColWidth = 243,
  showOnlyDifferences = false,
}: GlobalTotalsRowProps) {
  const totalsPerPlan = plans.map((plan) => getGlobalTotals(plan));
  const totalBudgets = totalsPerPlan.map((t) => t.totalBudget);
  const techCosts = totalsPerPlan.map((t) => t.techCosts);

  const rowHasDiff =
    hasAnyDifference<number>(totalBudgets) ||
    hasAnyDifference<number>(techCosts);

  if (showOnlyDifferences && !rowHasDiff) return null;

  return (
    <TableRow sx={{ height: '96px' }}>
      <TableCell
        sx={{
          width: firstColWidth,
          p: 2,
          bgcolor: bg,
          borderRight: '1px solid',
          borderColor: '#C7D8F9',
        }}
      >
        <Typography fontWeight={700} fontSize="18px">
          TOTAL BUDGET
        </Typography>
        <Typography fontWeight={700} fontSize="14px">
          TECH COSTS
        </Typography>
      </TableCell>

      {plans.map((plan, index) => {
        const totals = totalsPerPlan[index];

        const isBudgetDiff = isDifferentFromBase<number>(totalBudgets, index);
        const isTechDiff = isDifferentFromBase<number>(techCosts, index);

        return (
          <TableCell
            key={`global-${plan.id}`}
            sx={{
              p: 2,
              bgcolor: bg,
              borderLeft: '1px solid',
              borderColor: '#C7D8F9',
              verticalAlign: 'middle',
              textAlign: 'center',
            }}
          >
            <Typography
              fontWeight={700}
              fontSize="18px"
              sx={{ color: isBudgetDiff ? 'error.main' : '#525F81' }}
            >
              {formatBudget(totals.totalBudget)}
            </Typography>
            <Typography
              fontWeight={700}
              fontSize="16px"
              sx={{ color: isTechDiff ? 'error.main' : '#525F81' }}
            >
              {formatBudget(totals.techCosts)}
            </Typography>
          </TableCell>
        );
      })}
    </TableRow>
  );
}
